import { Text } from 'slate';
import { createBlockTexts, mapOffset } from './normalization/block-texts';
const isDecoratableElement = (node) => {
    return (node.type === 'paragraph' ||
        node.type === 'heading' ||
        node.type === 'tableCell');
};
const decorate = ([node, path,]) => {
    if (Text.isText(node)) {
        return [];
    }
    if (!isDecoratableElement(node)) {
        return [];
    }
    const mdastNode = node.mdastNode;
    if (mdastNode == null) {
        return [];
    }
    if (mdastNode.type !== 'paragraph' &&
        mdastNode.type !== 'heading' &&
        mdastNode.type !== 'tableCell') {
        return [];
    }
    const position = mdastNode.position;
    if (position == null) {
        return [];
    }
    if (position.start.offset == null) {
        return [];
    }
    // `mdast` could be obtained from parsing a text with preceding elements and its start offset cannot be 0.
    // Then, `lineHeadOffset` here is the offset of this element's first character
    // and will be used in the following to subtract from offsets of the child nodes.
    const lineHeadOffset = position.start.offset - (position.start.column - 1);
    const container = createBlockTexts(node);
    /**
     * Appends new `decoration` to the given `decorations`.
     * The `decoration`'s `anchor` and `focus` are calculated from `start` and `end` arguments
     * and the `start` and `end` are the offsets measured in the parent `node`,
     * obtained from remark parsing the whole text of the `node`.
     * Note that this method mutates `decorations`!
     */
    const appendDecoration = (decorations, start, end, mdast) => {
        const localStart = mapOffset(container, start);
        const localEnd = mapOffset(container, end);
        if (localStart && localEnd) {
            decorations.push({
                [mdast.type]: mdast,
                anchor: {
                    path: path.concat(localStart.path),
                    offset: localStart.offset,
                },
                focus: {
                    path: path.concat(localEnd.path),
                    offset: localEnd.offset,
                },
            });
        }
    };
    /**
     * Generates decorations based on the tokens in `content`,
     * which is an element in an AST created by remark
     * and appends the generated decorations to the given `decorations`.
     * Note that this method mutates `decorations`!
     */
    const generateDecorations = (content, decorations) => {
        const contentPosition = content.position;
        const contentStartOffset = contentPosition === null || contentPosition === void 0 ? void 0 : contentPosition.start.offset;
        const contentEndOffset = contentPosition === null || contentPosition === void 0 ? void 0 : contentPosition.end.offset;
        if (contentStartOffset != null && contentEndOffset != null) {
            const localContentStartOffset = contentStartOffset - lineHeadOffset;
            const localContentEndOffset = contentEndOffset - lineHeadOffset;
            // TODO: Modularize
            if (content.type === 'heading') {
                const firstChild = content.children[0];
                const lastChild = content.children[content.children.length - 1];
                if (firstChild && lastChild) {
                    const firstChildPosition = firstChild.position;
                    const lastChildPosition = lastChild.position;
                    const firstChildStartOffset = firstChildPosition === null || firstChildPosition === void 0 ? void 0 : firstChildPosition.start.offset;
                    const lastChildEndOffset = lastChildPosition === null || lastChildPosition === void 0 ? void 0 : lastChildPosition.end.offset;
                    if (firstChildStartOffset) {
                        appendDecoration(decorations, localContentStartOffset, firstChildStartOffset - lineHeadOffset, { type: 'headingMark' });
                    }
                    if (lastChildEndOffset) {
                        appendDecoration(decorations, lastChildEndOffset, localContentEndOffset - lineHeadOffset, { type: 'headingMark' });
                    }
                }
            }
            else if (content.type === 'emphasis' ||
                content.type === 'strong' ||
                content.type === 'delete' ||
                content.type === 'inlineCode') {
                appendDecoration(decorations, localContentStartOffset, localContentEndOffset, content);
            }
            else if (content.type === 'link') {
                appendDecoration(decorations, localContentStartOffset, localContentEndOffset, content);
            }
            else if (content.type === 'image') {
                appendDecoration(decorations, localContentStartOffset, localContentEndOffset, content);
            }
        }
        if (content.children) {
            content.children.forEach((child) => {
                generateDecorations(child, decorations);
            });
        }
    };
    const decorations = []; // `decorations` will be mutated!
    generateDecorations(mdastNode, decorations);
    // Block-level
    const parentMdastNode = node.parentMdastNode;
    if (parentMdastNode && mdastNode.position) {
        if (parentMdastNode.type === 'listItem') {
            const checkbox = parentMdastNode.checked !== null;
            if (checkbox) {
                const blockStartColumn = mdastNode.position.start.column;
                const checkboxOffset = blockStartColumn - 5;
                // For example,
                // "* [ ] content"
                //        ^
                //        blockStartColumn (1-indexed)
                //    ^
                //    checkboxOffset (0-indexed)
                appendDecoration(decorations, checkboxOffset, checkboxOffset + 3, {
                    type: 'listCheckbox',
                    checked: parentMdastNode.checked,
                });
            }
        }
    }
    return decorations;
};
export default decorate;
//# sourceMappingURL=decorate.js.map